import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  Star, 
  Instagram,
  Facebook,
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

// Logo image URL (provided by user)
const LOGO_URL = "https://customer-assets.emergentagent.com/job_deck-remodel-pro/artifacts/9n0jwtvl_1a6df596-41bf-408e-a280-3ad30c91f162.png";

// Project images from user with descriptions - v2
const PROJECT_IMAGES = [
  { src: "/services/exterior-trim-new.jpg", title: "Doors & Windows", description: "Quality doors, windows and trim work" },
  { src: "/services/new-deck.jpg", title: "Decks & Outdoor Living", description: "Custom decks, patios and outdoor spaces" },
  { src: "/services/7e777435-dbf0-4edc-adbc-dd6fedffabad.jpg", title: "Custom Projects", description: "Basement bars and entertainment areas" },
  { src: "/services/6a518040-e096-42ac-bae2-e6e15d6052a5.jpg", title: "& More", description: "Kitchens, bathrooms, additions and more" }
];

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#about", label: "About" },
    { href: "#testimonials", label: "Reviews" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav
      data-testid="navigation"
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled ? "bg-white shadow-lg py-2" : "bg-transparent py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Social Icons - Left Corner (permanent) */}
          <div className="flex items-center gap-3 relative z-10" data-testid="nav-social-links">
            <a
              href="https://www.facebook.com/profile.php?id=61561606993501"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 ${isScrolled ? 'bg-[#0A0A0A] text-white hover:bg-[#FACC15] hover:text-[#0A0A0A]' : 'bg-white/10 text-white hover:bg-[#FACC15] hover:text-[#0A0A0A]'}`}
              aria-label="Follow us on Facebook"
              data-testid="nav-social-facebook"
            >
              <Facebook size={20} />
            </a>
            <a
              href="https://www.instagram.com/kbbuildings"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 ${isScrolled ? 'bg-[#0A0A0A] text-white hover:bg-[#FACC15] hover:text-[#0A0A0A]' : 'bg-white/10 text-white hover:bg-[#FACC15] hover:text-[#0A0A0A]'}`}
              aria-label="Follow us on Instagram"
              data-testid="nav-social-instagram"
            >
              <Instagram size={20} />
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`nav-link text-sm font-medium uppercase tracking-wider transition-colors duration-300 ${isScrolled ? 'text-[#0A0A0A] hover:text-[#FACC15]' : 'text-white hover:text-[#FACC15]'}`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="tel:5085627115"
              className={`flex items-center gap-2 px-6 py-3 font-bold text-sm uppercase tracking-wider transition-all duration-300 ${isScrolled ? 'bg-[#0A0A0A] text-white hover:bg-[#FACC15] hover:text-[#0A0A0A]' : 'bg-[#FACC15] text-[#0A0A0A] hover:bg-white'}`}
              data-testid="nav-call-button"
            >
              <Phone size={16} />
              (508) 562-7115
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 transition-colors ${isScrolled ? 'text-[#0A0A0A]' : 'text-white'}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`mobile-menu md:hidden fixed top-20 right-0 w-full h-[calc(100vh-80px)] bg-[#0A0A0A] ${
          isMobileMenuOpen ? "open" : ""
        }`}
        data-testid="mobile-menu"
      >
        <div className="flex flex-col items-center justify-center h-full space-y-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-white text-2xl font-medium uppercase tracking-wider hover:text-[#FACC15] transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <a
            href="tel:5085627115"
            className="flex items-center gap-2 bg-[#FACC15] text-[#0A0A0A] px-8 py-4 font-bold text-lg uppercase tracking-wider"
          >
            <Phone size={20} />
            Call Now
          </a>
        </div>
      </div>
    </nav>
  );
};

// Hero Section Component
const HeroSection = () => {
  return (
    <section className="hero-section" data-testid="hero-section">
      {/* Video Background */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="video-background"
        poster="/hero-poster.jpg"
        data-testid="hero-video"
      >
        <source src="/hero-video.mp4" type="video/mp4" />
      </video>

      {/* Overlay with gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/50 to-[#0A0A0A]/90 z-[1]"></div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
        {/* Logo - Bigger with slow fade-in animation */}
        <img 
          src={LOGO_URL} 
          alt="KBBuildings Logo" 
          className="h-72 sm:h-80 md:h-96 lg:h-[28rem] w-auto mx-auto mb-8 opacity-0 animate-hero-logo-appear"
        />
        
        <p 
          className="text-lg sm:text-xl lg:text-2xl text-white/80 mb-4 opacity-0 animate-fade-in-up animation-delay-100 font-light tracking-wide"
          data-testid="hero-subtitle"
        >
          Building & Remodeling Contractor in Massachusetts
        </p>
        <a 
          href="tel:5085627115"
          className="inline-block text-[#FACC15] text-2xl sm:text-3xl font-bold mb-12 opacity-0 animate-fade-in-up animation-delay-200 hover:text-white transition-colors duration-300"
          data-testid="hero-phone-display"
        >
          (508) 562-7115
        </a>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 opacity-0 animate-fade-in-up animation-delay-300">
          <a
            href="#contact"
            className="group w-full sm:w-auto bg-[#FACC15] text-[#0A0A0A] px-10 py-5 font-bold uppercase tracking-wider hover:bg-white transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(250,204,21,0.3)] flex items-center justify-center gap-3"
            data-testid="hero-cta-estimate"
          >
            Get Free Estimate
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="tel:5085627115"
            className="group w-full sm:w-auto border-2 border-white text-white px-10 py-5 font-bold uppercase tracking-wider hover:bg-white hover:text-[#0A0A0A] transition-all duration-300 hover:-translate-y-2 flex items-center justify-center gap-3"
            data-testid="hero-cta-call"
          >
            <Phone size={20} className="group-hover:animate-pulse" />
            Call Now
          </a>
        </div>
      </div>
    </section>
  );
};

// Services Section Component
const ServicesSection = () => {
  const services = [
    "Finish Carpentry",
    "Decks",
    "Doors & Windows",
    "Siding",
    "Kitchens & Bathrooms",
    "General Contracting",
    "& More"
  ];

  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="services" 
      className="py-20 lg:py-32 bg-[#0A0A0A] overflow-hidden"
      ref={sectionRef}
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-[#FACC15] text-sm font-bold uppercase tracking-[0.3em] mb-4 block">What We Do</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="services-title">
            Our Services
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#8B5A2B] to-[#FACC15] mx-auto mt-6"></div>
        </div>

        {/* Services List - Horizontal */}
        <div className={`flex flex-wrap justify-center gap-4 mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {services.map((service, index) => (
            <a
              key={service}
              href="#contact"
              className={`group px-6 py-3 bg-[#171717] border border-[#262626] hover:border-[#FACC15] hover:bg-[#FACC15] transition-all duration-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              data-testid={`service-item-${index}`}
            >
              <span className="text-white group-hover:text-[#0A0A0A] font-semibold transition-colors duration-300">
                {service}
              </span>
            </a>
          ))}
        </div>

        {/* Horizontal Image Gallery with Arrows */}
        <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
          {/* Left Arrow */}
          <button 
            onClick={() => {
              const gallery = document.getElementById('gallery-scroll');
              if (gallery) gallery.scrollBy({ left: -400, behavior: 'smooth' });
            }}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-[#FACC15] text-[#0A0A0A] flex items-center justify-center hover:bg-white transition-colors duration-300 hidden md:flex"
            aria-label="Scroll left"
          >
            <ChevronLeft size={24} />
          </button>
          
          {/* Right Arrow */}
          <button 
            onClick={() => {
              const gallery = document.getElementById('gallery-scroll');
              if (gallery) gallery.scrollBy({ left: 400, behavior: 'smooth' });
            }}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-[#FACC15] text-[#0A0A0A] flex items-center justify-center hover:bg-white transition-colors duration-300 hidden md:flex"
            aria-label="Scroll right"
          >
            <ChevronRight size={24} />
          </button>

          {/* Gallery Container */}
          <div id="gallery-scroll" className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide items-end px-2 md:px-16">
            {PROJECT_IMAGES.map((img, index) => (
              <a 
                key={`gallery-${img.title}-${index}`} 
                href="#contact"
                className="relative flex-shrink-0 snap-start cursor-pointer group w-72 md:w-80 lg:w-96 bg-[#171717]"
              >
                {/* Image container - full image */}
                <div className="overflow-hidden">
                  <img 
                    src={img.src} 
                    alt={img.title}
                    className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                {/* Caption below image */}
                <div className="p-4 border-t border-[#262626] group-hover:bg-[#1a1a1a] transition-colors duration-300">
                  <h3 className="text-white font-bold text-lg mb-1 group-hover:text-[#FACC15] transition-colors">{img.title}</h3>
                  <p className="text-[#A3A3A3] text-sm">{img.description}</p>
                  <div className="mt-3 flex items-center text-[#FACC15] text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300">
                    Get Free Estimate <ArrowRight size={14} className="ml-2" />
                  </div>
                </div>
              </a>
            ))}
          </div>
          
          {/* Scroll indicator for mobile */}
          <div className="flex justify-center items-center gap-2 mt-4 md:hidden">
            <ChevronLeft size={20} className="text-[#FACC15]" />
            <span className="text-[#A3A3A3] text-sm">Swipe to view more</span>
            <ChevronRight size={20} className="text-[#FACC15]" />
          </div>
        </div>

        {/* CTA Button */}
        <div className={`text-center mt-12 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <a
            href="#contact"
            className="group inline-flex items-center gap-3 bg-[#FACC15] text-[#0A0A0A] px-10 py-5 font-bold uppercase tracking-wider hover:bg-white transition-all duration-300 hover:-translate-y-1"
          >
            Start Your Project Today
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

// About Section Component
const AboutSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="about" 
      className="py-20 lg:py-32 bg-[#171717] relative overflow-hidden"
      ref={sectionRef}
      data-testid="about-section"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#FACC15]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#8B5A2B]/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`}>
            <span className="text-[#FACC15] text-sm font-bold uppercase tracking-[0.3em] mb-4 block">Who We Are</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="about-title">
              About <span className="text-[#FACC15]">KBBuildings</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#8B5A2B] to-[#FACC15] mb-8"></div>
            <p className="text-white/70 text-lg leading-relaxed mb-6" data-testid="about-description">
              KBBuildings is a trusted Massachusetts-based contractor specializing in high-quality building and remodeling services. Known for reliability, craftsmanship, and fair pricing, we take pride in delivering results that last.
            </p>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              We understand how expensive projects can be, which is why we work closely with every homeowner to find affordable solutions that fit their budget. Our goal is to bring your vision to reality without breaking the bank.
            </p>
            <a
              href="#contact"
              className="group inline-flex items-center gap-3 bg-[#FACC15] text-[#0A0A0A] px-8 py-4 font-bold uppercase tracking-wider hover:bg-white transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(250,204,21,0.2)]"
              data-testid="about-cta"
            >
              Get Your Free Estimate
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          {/* Visual Element with Logo */}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0 rotate-0' : 'opacity-0 translate-x-20 rotate-3'}`}>
            <div className="relative">
              {/* Animated border */}
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-[#FACC15]/30 transition-all duration-500 hover:border-[#FACC15]/50"></div>
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#8B5A2B]/20"></div>
              
              <div className="bg-[#0A0A0A] p-10 lg:p-14 relative">
                {/* Logo watermark */}
                <div className="absolute top-4 right-4 opacity-10">
                  <img src={LOGO_URL} alt="" className="h-24 w-auto" />
                </div>
                <div className="space-y-8 relative">
                  {[
                    { text: "Licensed & Insured", delay: 0 },
                    { text: "Quality Craftsmanship", delay: 100 },
                    { text: "Fair & Transparent Pricing", delay: 200 },
                    { text: "Serving All of Massachusetts", delay: 300 }
                  ].map((item, index) => (
                    <div 
                      key={item.text}
                      className={`flex items-center gap-4 group transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
                      style={{ transitionDelay: `${500 + item.delay}ms` }}
                    >
                      <div className="w-4 h-4 bg-[#FACC15] transition-all duration-300 group-hover:rotate-45 group-hover:scale-125"></div>
                      <span className="text-white font-medium text-lg group-hover:text-[#FACC15] transition-colors">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section Component
const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="testimonials" 
      className="py-20 lg:py-32 bg-[#0A0A0A] relative overflow-hidden"
      ref={sectionRef}
      data-testid="testimonials-section"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 text-[300px] font-serif text-white">"</div>
        <div className="absolute bottom-20 right-20 text-[300px] font-serif text-white rotate-180">"</div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-[#FACC15] text-sm font-bold uppercase tracking-[0.3em] mb-4 block">Testimonials</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="testimonials-title">
            What Our Clients Say
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#8B5A2B] to-[#FACC15] mx-auto mt-6"></div>
        </div>

        <div className={`max-w-4xl mx-auto transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
          <div className="testimonial-card bg-gradient-to-br from-[#171717] via-[#1a1a1a] to-[#0A0A0A] border border-[#8B5A2B]/30 p-10 lg:p-16 text-center relative group hover:border-[#FACC15]/50 transition-all duration-500" data-testid="testimonial-card">
            {/* Decorative corners */}
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-[#FACC15]/30 group-hover:border-[#FACC15] transition-colors duration-500"></div>
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-[#FACC15]/30 group-hover:border-[#FACC15] transition-colors duration-500"></div>
            
            {/* Stars with animation */}
            <div className="flex justify-center gap-2 mb-8 relative z-10" data-testid="testimonial-stars">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  className={`w-7 h-7 text-[#FACC15] fill-[#FACC15] transition-all duration-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`}
                  style={{ transitionDelay: `${600 + i * 100}ms` }}
                />
              ))}
            </div>

            {/* Quote */}
            <blockquote className="text-white text-xl lg:text-2xl leading-relaxed mb-8 relative z-10 font-light" data-testid="testimonial-quote">
              "I had them come and do my deck out back and stairs out front a few years ago, they did it in a timely manner at a very reasonably priced, and it has held up beautifully!! <span className="font-bold text-[#FACC15]">HIGHLY RECOMMEND</span>"
            </blockquote>

            {/* Thumbs up with hover effect */}
            <div className="text-5xl mb-6 transition-transform duration-300 hover:scale-125">👍👍</div>

            {/* Divider */}
            <div className="w-24 h-1 bg-gradient-to-r from-[#8B5A2B] to-[#FACC15] mx-auto"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Banner Section
const CTABanner = () => {
  return (
    <section className="py-16 bg-[#FACC15] relative overflow-hidden">
      {/* Background logo pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-10">
          <img src={LOGO_URL} alt="" className="h-40 w-auto" />
        </div>
        <div className="absolute bottom-0 right-10">
          <img src={LOGO_URL} alt="" className="h-40 w-auto" />
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] mb-6">
          Ready to Start Your Project?
        </h2>
        <p className="text-[#0A0A0A]/70 text-lg mb-8 max-w-2xl mx-auto">
          Get a free estimate today and let us bring your vision to life with quality craftsmanship.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="#contact"
            className="w-full sm:w-auto bg-[#0A0A0A] text-white px-10 py-4 font-bold uppercase tracking-wider hover:bg-white hover:text-[#0A0A0A] transition-all duration-300"
          >
            Get Free Estimate
          </a>
          <a
            href="tel:5085627115"
            className="w-full sm:w-auto border-2 border-[#0A0A0A] text-[#0A0A0A] px-10 py-4 font-bold uppercase tracking-wider hover:bg-[#0A0A0A] hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
          >
            <Phone size={18} />
            (508) 562-7115
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section Component
const ContactSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formStatus, setFormStatus] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("submitting");
    const form = e.target;
    const formData = new FormData(form);
    
    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });
      
      if (response.ok) {
        setFormStatus("success");
        form.reset();
      } else {
        console.error("Form submission failed:", response.status, response.statusText);
        setFormStatus("error");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setFormStatus("error");
    }
  };

  return (
    <section 
      id="contact" 
      className="py-20 lg:py-32 bg-[#171717]"
      ref={sectionRef}
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="contact-title">
            Get Your Free Estimate
          </h2>
          <p className="text-[#A3A3A3] text-lg max-w-2xl mx-auto">
            Ready to start your project? Fill out the form below or give us a call.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Form */}
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <form 
              name="contact" 
              method="POST" 
              action="/"
              data-netlify="true"
              netlify-honeypot="bot-field"
              className="contact-form space-y-6"
              onSubmit={handleSubmit}
              data-testid="contact-form"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>
                  Don't fill this out: <input name="bot-field" />
                </label>
              </p>

              <div>
                <label htmlFor="name" className="sr-only">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Your Name *"
                  required
                  aria-required="true"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label htmlFor="phone" className="sr-only">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="Phone Number *"
                  required
                  aria-required="true"
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="sr-only">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Email Address *"
                  required
                  aria-required="true"
                  data-testid="contact-email-input"
                />
              </div>

              <div className="relative">
                <label htmlFor="project-type" className="sr-only">Project Type</label>
                <select
                  id="project-type"
                  name="project-type"
                  required
                  aria-required="true"
                  className="appearance-none"
                  data-testid="contact-project-select"
                >
                  <option value="">Select Project Type *</option>
                  <option value="finish-carpentry">Finish Carpentry</option>
                  <option value="decks">Decks</option>
                  <option value="interior-trim">Interior Trim</option>
                  <option value="exterior-trim">Exterior Trim</option>
                  <option value="siding">Siding</option>
                  <option value="windows">Windows</option>
                  <option value="basement">Finish Basement</option>
                  <option value="other">Other</option>
                </select>
                <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none" size={20} />
              </div>

              <div>
                <label htmlFor="message" className="sr-only">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  placeholder="Tell us about your project..."
                  data-testid="contact-message-input"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={formStatus === "submitting"}
                className={`w-full bg-[#FACC15] text-[#0A0A0A] px-8 py-4 font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${formStatus === "submitting" ? "opacity-70 cursor-not-allowed" : "hover:bg-[#EAB308] hover:-translate-y-1"}`}
                data-testid="contact-submit-button"
              >
                {formStatus === "submitting" ? "Sending..." : "Request Free Estimate"}
                {formStatus !== "submitting" && <ArrowRight size={18} />}
              </button>

              {formStatus === "success" && (
                <p className="text-green-500 text-center" data-testid="form-success-message">
                  Thank you! We'll be in touch soon.
                </p>
              )}
              {formStatus === "error" && (
                <p className="text-red-500 text-center" data-testid="form-error-message">
                  Something went wrong. Please call us directly.
                </p>
              )}
            </form>
          </div>

          {/* Contact Info */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="bg-[#0A0A0A] p-8 lg:p-12 h-full">
              <h3 className="text-2xl font-bold text-white mb-8">Contact Information</h3>
              
              <div className="space-y-8">
                <a 
                  href="tel:5085627115" 
                  className="flex items-start gap-4 group"
                  data-testid="contact-info-phone"
                >
                  <div className="w-12 h-12 bg-[#FACC15] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Phone className="text-[#0A0A0A]" size={20} />
                  </div>
                  <div>
                    <p className="text-[#A3A3A3] text-sm uppercase tracking-wider mb-1">Phone</p>
                    <p className="text-white text-xl font-bold">(508) 562-7115</p>
                  </div>
                </a>

                <a 
                  href="mailto:kbbuildings.llc@gmail.com" 
                  className="flex items-start gap-4 group"
                  data-testid="contact-info-email"
                >
                  <div className="w-12 h-12 bg-[#FACC15] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Mail className="text-[#0A0A0A]" size={20} />
                  </div>
                  <div>
                    <p className="text-[#A3A3A3] text-sm uppercase tracking-wider mb-1">Email</p>
                    <p className="text-white text-lg font-medium break-all">kbbuildings.llc@gmail.com</p>
                  </div>
                </a>

                <div className="flex items-start gap-4" data-testid="contact-info-location">
                  <div className="w-12 h-12 bg-[#FACC15] flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#0A0A0A]" size={20} />
                  </div>
                  <div>
                    <p className="text-[#A3A3A3] text-sm uppercase tracking-wider mb-1">Service Area</p>
                    <p className="text-white text-lg font-medium">Massachusetts</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-12 pt-8 border-t border-[#262626]">
                <p className="text-[#A3A3A3] text-sm uppercase tracking-wider mb-4">Follow Us</p>
                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/kbbuildings"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link w-12 h-12 border border-[#262626] flex items-center justify-center text-white hover:border-[#FACC15]"
                    aria-label="Follow us on Instagram"
                    data-testid="social-instagram"
                  >
                    <Instagram size={20} />
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=61561606993501"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link w-12 h-12 border border-[#262626] flex items-center justify-center text-white hover:border-[#FACC15]"
                    aria-label="Follow us on Facebook"
                    data-testid="social-facebook"
                  >
                    <Facebook size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Large centered logo */}
        <div className="flex justify-center mb-12">
          <img 
            src={LOGO_URL} 
            alt="KBBuildings Logo" 
            className="h-32 md:h-40 lg:h-48 w-auto object-contain"
            loading="lazy"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center border-t border-gray-200 pt-8">
          {/* Contact Info */}
          <div className="text-center md:text-left">
            <p className="text-gray-500 text-sm uppercase tracking-wider mb-2">Contact Us</p>
            <a href="tel:5085627115" className="text-[#0A0A0A] font-bold text-xl hover:text-[#FACC15] transition-colors">
              (508) 562-7115
            </a>
          </div>

          {/* Location */}
          <div className="text-center">
            <p className="text-gray-500 text-sm uppercase tracking-wider mb-2">Service Area</p>
            <p className="text-[#0A0A0A] font-bold text-lg">Massachusetts</p>
          </div>

          {/* Social Links */}
          <div className="flex justify-center md:justify-end gap-4">
            <a
              href="https://www.instagram.com/kbbuildings"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-[#0A0A0A] flex items-center justify-center text-white hover:bg-[#FACC15] hover:text-[#0A0A0A] transition-all duration-300"
              aria-label="Instagram"
            >
              <Instagram size={22} />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61561606993501"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-[#0A0A0A] flex items-center justify-center text-white hover:bg-[#FACC15] hover:text-[#0A0A0A] transition-all duration-300"
              aria-label="Facebook"
            >
              <Facebook size={22} />
            </a>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-200 text-center">
          <p className="text-gray-500 text-sm">
            © {currentYear} KBBuildings LLC. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <AboutSection />
        <TestimonialsSection />
        <CTABanner />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
